import { useEffect, useMemo, useRef, useState } from 'react';

import { StrapiImageFragment } from '../../graphql.schema';
import debounce from 'lodash/debounce';


export const useImageResize = (image: StrapiImageFragment) => {
  const imageWrapper = useRef<HTMLDivElement>(null);
  const [imageWrapperWidth, setImageWrapperWidth] = useState<number | null>(null);

  useEffect(() => {
    const updateImageSize = () => {
      if (imageWrapper.current) {
        setImageWrapperWidth(imageWrapper.current.offsetWidth);
      }
    };

    if (imageWrapper.current) {
      updateImageSize();
    }

    window.addEventListener('resize', debounce(updateImageSize, 600));


    return () => window.removeEventListener('resize', updateImageSize);
  }, [imageWrapper.current]);

  const imageStyle = useMemo(() => {
    if (imageWrapperWidth) {
      return {
        maxHeight: `${imageWrapperWidth}px`,
        maxWidth: `${imageWrapperWidth}px`,
      };
    }
  }, [imageWrapperWidth]);

  const { imageUrl, imageWidth, imageHeight } = useMemo(() => {
    const original = { imageUrl: image?.url, imageWidth: image?.width, imageHeight: image?.height };
    const formats = image?.formats;
    const large = formats?.large
      ? {
          imageUrl: formats.large.url,
          imageWidth: formats.large,
          imageHeight: formats.large.height,
        }
      : null;
    const medium = formats?.medium
      ? {
          imageUrl: formats.medium.url,
          imageHeight: formats.medium.height,
          imageWidth: formats.medium.width,
        }
      : null;
    const small = formats?.small
      ? {
          imageUrl: formats.small.url,
          imageHeight: formats.small.height,
          imageWidth: formats.small.width,
        }
      : null;

    if (!imageWrapperWidth || imageWrapperWidth > 750) {
      return large ?? medium ?? original;
    } else if (imageWrapperWidth > 500) {
      return medium ?? original;
    } else {
      return small ?? original;
    }
  }, [imageWrapperWidth]);

  return {
    imageUrl,
    imageWidth,
    imageHeight,
    imageStyle,
    imageWrapper,
    imageWrapperWidth,
    setImageWrapperWidth
  };
};
